import React, { useEffect, useState } from "react";
import { api, euroFormat, dateFormat, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Briefcase, Download, AlertCircle } from "lucide-react";

export default function TippgeberCockpit() {
  const { user, refresh } = useAuth();
  const [provisionen, setProvisionen] = useState([]);
  const [gutschriften, setGutschriften] = useState([]);
  const [gewerbeDaten, setGewerbeDaten] = useState(null);
  const [widerspruchModal, setWiderspruchModal] = useState(null);
  const [grund, setGrund] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    const [p, g, gd] = await Promise.all([
      api.get("/me/provisionen"),
      api.get("/me/gutschriften"),
      api.get("/me/gewerbe-daten"),
    ]);
    setProvisionen(p.data); setGutschriften(g.data); setGewerbeDaten(gd.data);
  };
  useEffect(() => { load(); }, []);

  const zustimmen = async () => {
    await api.post("/me/gutschriftverfahren-zustimmen");
    await refresh();
  };

  const widerspruch = async () => {
    try {
      await api.post(`/me/gutschriften/${widerspruchModal.id}/widerspruch`, { grund });
      setWiderspruchModal(null); setGrund("");
      await load();
      setMsg("Widerspruch eingelegt.");
    } catch (e) {
      setMsg(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const summe = (filter) => provisionen.filter(filter).reduce((s, p) => s + (p.provisionsbetrag_netto || 0), 0);
  const dieses_jahr = new Date().getFullYear();
  const ausgezahlt = summe((p) => p.status === "ausgezahlt");
  const offen = summe((p) => p.status !== "ausgezahlt");
  const jahr = summe((p) => p.erstellt_am?.startsWith(dieses_jahr.toString()));
  const gesamt = summe(() => true);

  return (
    <div className="space-y-6" data-testid="tippgeber-cockpit">
      <div className="flex items-center gap-3">
        <Briefcase className="text-falke-orange" size={28} />
        <div>
          <h1 className="text-3xl font-heading font-bold text-falke-blue">Tippgeber-Cockpit</h1>
          <p className="text-falke-anthracite/70">Hallo {user.name} · Provisionssatz: <strong className="text-falke-orange">2 % vom Netto-Auftragswert</strong></p>
        </div>
      </div>

      {!user.zustimmung_gutschriftverfahren_am && (
        <div className="falke-card p-6 border-l-4 border-falke-orange" data-testid="zustimmung-card">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-falke-orange flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-falke-blue">Zustimmung Gutschriftverfahren erforderlich</div>
              <p className="text-sm text-falke-anthracite/80 mt-1 mb-3">Damit FalkeTek dir Gutschriften nach §14 Abs. 2 UStG ausstellen darf, benötigen wir einmalig deine Zustimmung.</p>
              <button onClick={zustimmen} className="btn-primary !py-2" data-testid="zustimmung-btn">Zustimmen</button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI label={`Provision ${dieses_jahr}`} val={euroFormat(jahr)} />
        <KPI label="Ausgezahlt" val={euroFormat(ausgezahlt)} success />
        <KPI label="Offen" val={euroFormat(offen)} orange />
        <KPI label="Gesamt (lifetime)" val={euroFormat(gesamt)} />
      </div>

      <div className="falke-card p-6">
        <h3 className="font-heading text-lg text-falke-blue mb-3">Provisionen</h3>
        {provisionen.length === 0 ? (
          <div className="text-sm text-falke-anthracite/60 py-6 text-center">Noch keine Provisionen erfasst.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-falke-blue/70 text-xs uppercase tracking-wider">
                <tr><th className="py-2">Datum</th><th>Auftragswert (netto)</th><th>Satz</th><th>Provision</th><th>Status</th></tr>
              </thead>
              <tbody>
                {provisionen.map((p) => (
                  <tr key={p.id} className="border-t border-falke-blue/10" data-testid={`provision-${p.id}`}>
                    <td className="py-3">{dateFormat(p.erstellt_am)}</td>
                    <td className="tabular">{euroFormat(p.auftragswert_netto)}</td>
                    <td className="tabular">{(p.provisionssatz * 100).toFixed(0)} %</td>
                    <td className="tabular font-bold text-falke-orange">{euroFormat(p.provisionsbetrag_netto)}</td>
                    <td><span className="status-badge bg-falke-blue/10 text-falke-blue">{p.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="falke-card p-6">
        <h3 className="font-heading text-lg text-falke-blue mb-3">Erhaltene Gutschriften</h3>
        {gutschriften.length === 0 ? (
          <div className="text-sm text-falke-anthracite/60 py-6 text-center">Noch keine Gutschriften.</div>
        ) : (
          <div className="space-y-3">
            {gutschriften.map((g) => (
              <div key={g.id} className="flex flex-wrap items-center gap-3 p-4 rounded-xl border border-falke-blue/10" data-testid={`gutschrift-${g.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-falke-blue">{g.gutschriftnummer}</div>
                  <div className="text-xs text-falke-anthracite/60">{dateFormat(g.ausstellungsdatum)} · {euroFormat(g.summe_brutto)}</div>
                </div>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/gutschriften/${g.id}/pdf`} target="_blank" rel="noreferrer"
                  className="btn-outline !py-2 inline-flex items-center gap-1" data-testid={`gutschrift-download-${g.id}`}>
                  <Download size={16} /> PDF
                </a>
                {g.status === "versendet" && (
                  <button onClick={() => setWiderspruchModal(g)} className="text-sm text-rose-600 underline" data-testid={`widerspruch-btn-${g.id}`}>Widersprechen</button>
                )}
                {g.status !== "versendet" && <span className="status-badge bg-rose-100 text-rose-800">{g.status}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {gewerbeDaten && (
        <div className="falke-card p-6 text-sm text-falke-anthracite/80">
          <h3 className="font-heading text-lg text-falke-blue mb-3">Stammdaten</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><span className="text-falke-blue/60">Firmenname:</span> {gewerbeDaten.firmenname}</div>
            <div><span className="text-falke-blue/60">Steuernummer:</span> {gewerbeDaten.steuernummer}</div>
            <div className="sm:col-span-2"><span className="text-falke-blue/60">Adresse:</span> {gewerbeDaten.firmenadresse}</div>
            <div><span className="text-falke-blue/60">IBAN:</span> {gewerbeDaten.iban}</div>
            <div><span className="text-falke-blue/60">BIC:</span> {gewerbeDaten.bic}</div>
          </div>
          <div className="mt-4 p-3 rounded-xl bg-falke-blue/[0.04] text-xs">Du bist gewerblicher Empfehlungspartner und versteuerst deine Einnahmen selbst.</div>
        </div>
      )}

      {widerspruchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="widerspruch-modal">
          <div className="bg-white max-w-md w-full rounded-2xl p-6">
            <h3 className="font-heading text-xl text-falke-blue mb-2">Widerspruch zu {widerspruchModal.gutschriftnummer}</h3>
            <textarea rows={4} value={grund} onChange={(e) => setGrund(e.target.value)} placeholder="Bitte Grund angeben" className="w-full px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="widerspruch-grund" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setWiderspruchModal(null)} className="btn-outline">Abbrechen</button>
              <button onClick={widerspruch} className="btn-primary" data-testid="widerspruch-bestaetigen">Widerspruch senden</button>
            </div>
          </div>
        </div>
      )}
      {msg && <div className="text-sm text-falke-blue">{msg}</div>}
    </div>
  );
}

function KPI({ label, val, success, orange }) {
  return (
    <div className="falke-card p-5">
      <div className="text-xs text-falke-blue/60 uppercase tracking-wider">{label}</div>
      <div className={`font-heading text-2xl mt-1 tabular font-bold ${success ? "text-falke-success" : orange ? "text-falke-orange" : "text-falke-blue"}`}>{val}</div>
    </div>
  );
}
